import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, RefreshCw, Users, Clock, AlertCircle, Search } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { UserTable } from './UserTable';
import { UserEditDialog } from './UserEditDialog';
import { UserStatistics } from './UserStatistics';
import { NetworkManagement } from './NetworkManagement';
import { DomainManagementDialog } from './DomainManagementDialog';
import { User, Network, Role, UserType, UserStatus } from '../../types';
import axiosInstance from '../../lib/api/axiosInstance';
import { Button } from '../ui/button';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Input } from '../ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';

// Interface mise à jour pour refléter la structure de Prisma et Firebase
interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
  role: Role;
  type: UserType;
  status: UserStatus;
  networks?: string[];
  // Nouveaux champs pour l'association de contact
  contactAssociation?: {
    type: 'none' | 'create' | 'associate';
    contactId?: number;
    contactData?: {
      name: string;
      email: string;
      role: string;
      phone?: string;
      notes?: string;
    };
  };
}

export const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser, startImpersonation } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [availableNetworks, setAvailableNetworks] = useState<Network[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshClicked, setRefreshClicked] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [sortField, setSortField] = useState<keyof User>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [roleFilter, setRoleFilter] = useState('all');
  const [networkFilter, setNetworkFilter] = useState('all');
  
  // États pour la gestion des domaines
  const [isDomainDialogOpen, setIsDomainDialogOpen] = useState(false);
  const [selectedNetworkForDomains, setSelectedNetworkForDomains] = useState<Network | null>(null);

  const fetchNetworks = async () => {
    try {
      const response = await axiosInstance.get<Network[]>('/networks');
      setAvailableNetworks(response.data || []);
    } catch (error) {
      console.error('Error fetching networks:', error);
      setAvailableNetworks([]);
    }
  };

  const fetchUsers = async () => {
    setIsLoading(true);
    setApiError(null);
    try {
      const response = await axiosInstance.get<User[]>('/users');
      const userData = Array.isArray(response.data) ? response.data : [];
      console.log('🔍 fetchUsers response:', userData);
      userData.forEach(user => {
        console.log(`User ${user.email} networks:`, user.networks);
      });
      setUsers(userData);
      setFilteredUsers(userData);
      setLastUpdated(new Date());
    } catch (error: any) {
      console.error('Error fetching users:', error);
      const errorMessage = error.response?.data?.message || t('common.errorOccurred');
      setApiError(errorMessage);
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchNetworks();
    
    // Refresh automatique toutes les 60 secondes
    const interval = setInterval(() => {
      fetchUsers();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Filtrer les utilisateurs en fonction de la recherche et des filtres
    let result = users;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(user => 
        (user.name?.toLowerCase().includes(query) || false) ||
        (user.email?.toLowerCase().includes(query) || false)
      );
    }
    
    if (roleFilter !== 'all') {
      result = result.filter(user => user.role === roleFilter);
    }
    
    if (networkFilter !== 'all') {
      result = result.filter(user => 
        user.networks && user.networks.includes(networkFilter)
      );
    }
    
    // Tri des résultats
    result = [...result].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (!aValue && !bValue) return 0;
      if (!aValue) return sortDirection === 'asc' ? 1 : -1;
      if (!bValue) return sortDirection === 'asc' ? -1 : 1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      return 0;
    });
    
    setFilteredUsers(result);
  }, [users, searchQuery, roleFilter, networkFilter, sortField, sortDirection]);

  const handleRefresh = async () => {
    setRefreshClicked(true);
    try {
      await fetchUsers();
    } catch (error) {
      console.error('Error refreshing users:', error);
    } finally {
      setRefreshClicked(false);
    }
  };

  const validateForm = (data: UserFormData) => {
    const newErrors: Record<string, string> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!data.email || !emailRegex.test(data.email)) {
      newErrors.email = t('userManagement.errors.invalidEmail');
    }
    if (!data.firstName?.trim()) {
      newErrors.firstName = t('userManagement.errors.firstNameRequired');
    }
    if (!data.lastName?.trim()) {
      newErrors.lastName = t('userManagement.errors.lastNameRequired');
    }
    if (!selectedUser && !data.password?.trim()) {
      newErrors.password = t('userManagement.errors.passwordRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (formData: UserFormData) => {
    if (!validateForm(formData)) return;

    try {
      if (selectedUser) {
        // Édition d'un utilisateur existant
        await axiosInstance.put(`/users/${selectedUser.id}`, {
          email: formData.email,
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          role: formData.role,
          networks: formData.networks,
          contactAssociation: formData.contactAssociation
        });
      } else {
        // Création d'un nouvel utilisateur
        await axiosInstance.post('/users/onboard', {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          password: formData.password,
          role: formData.role,
          networks: formData.networks,
          contactAssociation: formData.contactAssociation
        });
      }
      fetchUsers();
      setOpen(false);
      setSelectedUser(null);
    } catch (error: any) {
      console.error('Error saving user:', error);
      // Gestion des erreurs spécifiques Firebase/Backend
      if (error.response?.data?.error === 'Email already in use') {
        setErrors({...errors, email: t('userManagement.errors.emailInUse')});
      } else {
        setApiError(t('common.errorOccurred'));
      }
    }
  };

  const handleDelete = async (userId: number | string) => {
    if (window.confirm(t('userManagement.confirmDelete'))) {
      try {
        await axiosInstance.delete(`/users/${userId}`);
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        setApiError(t('userManagement.deleteError'));
      }
    }
  };

    const handleImpersonate = async (user: User) => {
    if (window.confirm(t('userManagement.confirmImpersonate', { name: user.name }))) {
      try {
        await startImpersonation(user);
      } catch (error) {
        console.error('Error starting impersonation:', error);
      }
    }
  };

  const handleManageDomains = (network: Network) => {
    setSelectedNetworkForDomains(network);
    setIsDomainDialogOpen(true);
  };

  // Fonctions pour la gestion des réseaux
  const handleNetworkAdd = async (network: { name: string; bqKey: string }) => {
    try {
      const response = await axiosInstance.post<Network>('/networks', network);
      setAvailableNetworks(prev => [...prev, response.data]);
    } catch (error) {
      console.error('Error adding network:', error);
      setApiError(t('networkManagement.addError'));
    }
  };

  const handleNetworkEdit = async (oldName: string, network: { name: string; bqKey: string }) => {
    try {
      const existingNetwork = availableNetworks.find(n => n.name === oldName);
      if (!existingNetwork) return;
      
      const response = await axiosInstance.put<Network>(`/networks/${existingNetwork.id}`, network);
      setAvailableNetworks(prev => 
        prev.map(n => n.id === existingNetwork.id ? response.data : n)
      );
    } catch (error) {
      console.error('Error editing network:', error);
      setApiError(t('networkManagement.editError'));
    }
  };

  const handleNetworkDelete = async (networkName: string) => {
    try {
      const networkToDelete = availableNetworks.find(n => n.name === networkName);
      if (!networkToDelete) return;
      
      await axiosInstance.delete(`/networks/${networkToDelete.id}`);
      setAvailableNetworks(prev => prev.filter(n => n.id !== networkToDelete.id));
    } catch (error) {
      console.error('Error deleting network:', error);
      setApiError(t('networkManagement.deleteError'));
    }
  };

  const handleSort = (field: keyof User) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Vérifier si l'utilisateur courant est admin
  if (!currentUser || currentUser.role !== 'ADMIN') {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-6">
            <div className="text-destructive text-xl font-semibold">
              {t('common.accessDenied')}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading && users.length === 0) {
    return (
      <div className="p-6 space-y-6">
        <Card className="border-none shadow-sm dark:shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <CardTitle className="text-2xl">{t('userManagement.title')}</CardTitle>
            </div>
            <CardDescription>{t('common.loading')}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="p-6 space-y-6">
        <Card className="border-none shadow-sm dark:shadow-lg">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  <CardTitle className="text-2xl">{t('userManagement.title')}</CardTitle>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <Alert variant="destructive">
              <AlertCircle className="h-5 w-5" />
              <AlertTitle>{t('common.error')}</AlertTitle>
              <AlertDescription>
                {apiError}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRefresh}
                  className="ml-2"
                >
                  {t('common.retry')}
                </Button>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-7rem)] overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full p-6">
        {/* Colonne de gauche - Statistiques + Gestion des réseaux */}
        <div className="lg:col-span-1 flex flex-col overflow-hidden">
          {/* Statistiques avec hauteur fixe */}
          <div className="flex-shrink-0 mb-6">
            <UserStatistics users={users} networks={availableNetworks} />
          </div>
          
          {/* Gestion des réseaux avec scroll */}
          <div className="flex-1 min-h-0">
            <NetworkManagement
              networks={availableNetworks}
              onNetworkAdd={handleNetworkAdd}
              onNetworkEdit={handleNetworkEdit}
              onNetworkDelete={handleNetworkDelete}
              onRefresh={fetchNetworks}
              onManageDomains={handleManageDomains}
              isLoading={isLoading}
            />
          </div>
        </div>

        {/* Colonne de droite - Gestion des utilisateurs */}
        <div className="lg:col-span-2 overflow-hidden">
          <Card className="border-none shadow-sm dark:shadow-lg h-full flex flex-col">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    <CardTitle className="text-2xl">{t('userManagement.title')}</CardTitle>
                  </div>
                  {lastUpdated && (
                    <CardDescription className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {t('common.lastUpdated')}: {lastUpdated.toLocaleTimeString()}
                    </CardDescription>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleRefresh}
                    disabled={isLoading || refreshClicked}
                    className="h-9 w-9 rounded-full transition-all"
                    aria-label={t('common.refresh')}
                  >
                    <RefreshCw className={`h-4 w-4 ${(isLoading || refreshClicked) ? 'animate-spin text-primary' : ''}`} />
                    <span className="sr-only">{t('common.refresh')}</span>
                  </Button>
                  <Button 
                    onClick={() => {
                      setSelectedUser(null);
                      setErrors({});
                      setOpen(true);
                    }}
                    className="h-9 bg-primary/90 hover:bg-primary transition-colors"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('userManagement.addUser')}
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-4 flex-1 overflow-hidden flex flex-col">
              <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder={t('userManagement.search')}
                    className="pl-8 bg-muted/50 focus:bg-background transition-colors"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <Select 
                    value={roleFilter}
                    onValueChange={setRoleFilter}
                  >
                    <SelectTrigger className="h-9 w-[150px] text-xs">
                      <SelectValue placeholder={t('userManagement.roleFilter')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('common.all')}</SelectItem>
                      <SelectItem value={Role.ADMIN}>Admin</SelectItem>
                      <SelectItem value={Role.CLIENT}>Client</SelectItem>
                      <SelectItem value={Role.PARTNER}>Partner</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select 
                    value={networkFilter}
                    onValueChange={setNetworkFilter}
                  >
                    <SelectTrigger className="h-9 w-[150px] text-xs">
                      <SelectValue placeholder={t('userManagement.networkFilter')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('common.all')}</SelectItem>
                      {availableNetworks.map(network => (
                        <SelectItem key={network.id} value={network.name}>{network.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {searchQuery && (
                  <p className="text-sm text-muted-foreground whitespace-nowrap">
                    {filteredUsers.length} / {users.length} {t('userManagement.found')}
                  </p>
                )}
              </div>

              <div className="bg-muted/20 backdrop-blur-[1px] rounded-lg p-0.5 border border-border/40 shadow-sm flex-1 overflow-hidden">
                <UserTable
                  users={filteredUsers}
                  isLoading={isLoading}
                  onEdit={(user) => {
                    setSelectedUser(user);
                    setErrors({});
                    setOpen(true);
                  }}
                  onDelete={handleDelete}
                  onImpersonate={handleImpersonate}
                  availableNetworks={availableNetworks}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <UserEditDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setSelectedUser(null);
          setErrors({});
        }}
        onSubmit={handleSubmit}
        user={selectedUser}
        availableNetworks={availableNetworks}
        errors={errors}
      />

      <DomainManagementDialog
        open={isDomainDialogOpen}
        onClose={() => {
          setIsDomainDialogOpen(false);
          setSelectedNetworkForDomains(null);
        }}
        network={selectedNetworkForDomains}
      />
    </div>
  );
};
